import * as angular from 'angular';
import { ConfigService } from '../App/services/mx-config-service';

export type WorkspaceModuleConfig = {
	dependencies?: string[];
	config?: () => void;
	run?: () => void;
};

export class WorkspaceLoader {
	public static readonly $inject = ['mx.shell.Config'];
	public modules: string[] = [];

	constructor(private readonly configService: ConfigService) {}

	public registerModule(moduleName: string) {
		this.modules.push(moduleName);
	}

	public createModule(moduleName: string, config: WorkspaceModuleConfig = {}) {
		const module = angular.module(moduleName, config.dependencies || []);

		if (config.config) {
			module.config(config.config);
		}

		if (config.run) {
			module.run(config.run);
		}

		return module.name;
	}

	private async _loadDevFrontendWorkspaceFiles(devFrontendWorkspaces: string[]) {
		let files: string[] = [];

		for (let i = 0; i < devFrontendWorkspaces.length; i++) {
			const workspaceName = devFrontendWorkspaces[i];

			const response = await fetch(`./workspaces/${workspaceName}/workspace.json`);

			if (!response.ok) {
				throw new Error(`dev frontend workspace ${workspaceName} is unavailable.`);
			}

			const result = (await response.json()) as { resources: string[] };

			files = files.concat(result.resources.map((fileName) => `workspaces/${workspaceName}/${fileName}`));
		}

		return files;
	}

	// NOTE: Load custom scripts asynchronous and DOESN'T GARANTEE an order of execution.
	public async loadCustomWorkspaces(token: string) {
		const api = this.configService.get('restHosts').default;
		let devFrontendWorkspaces = this.configService.get('devFrontendWorkspaces');
		devFrontendWorkspaces = Array.isArray(devFrontendWorkspaces) ? devFrontendWorkspaces : [];
		const devMode = this.configService.get('mode') === 'dev';

		const response = await fetch(api + '/api/customWorkspace/all', {
			headers: {
				Authorization: 'Bearer ' + token,
			},
			mode: 'cors',
			credentials: 'include',
		});

		if (!response.ok) {
			throw new Error('customworkspaces service is unavailable.');
		}

		let result = (await response.json()) as string[];

		const devFrontendWorkspacesFiles = await this._loadDevFrontendWorkspaceFiles(devFrontendWorkspaces);
		const version = Date.now();

		// if file is already present in /wm directory and returned by customworkspace service then remove duplicate
		devFrontendWorkspacesFiles.forEach((frontFile) => {
			if (!result.some((backFile) => backFile.startsWith(frontFile))) {
				result.push(`${frontFile}?version=${version}`);
			}
		});

		const imgHost = this.configService.get('imgHosts')?.default;
		if (devMode && imgHost) {
			result = result.map((file) => {
				return devFrontendWorkspacesFiles.some((frontFile) => file.startsWith(frontFile)) ? file : imgHost + '/wm/' + file;
			});
		}

		await Promise.all(
			result.map(async (url) => {
				const itemExtension = url.split('?')[0].split('.').pop();
				if (itemExtension === 'js') {
					return this._loadJs(url);
				} else if (itemExtension === 'css') {
					this._loadCss(url);
				}
				return;
			})
		);
	}

	private _loadJs(url: string) {
		return new Promise((resolve, reject) => {
			const newScript = document.createElement('script');
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			newScript.onerror = (error) => reject(`${error.target.src} was not loaded`);
			newScript.onload = resolve;
			document.head.appendChild(newScript);
			newScript.src = url;
		});
	}

	private _loadCss(url: string) {
		const newLink = document.createElement('link');
		newLink.rel = 'stylesheet';
		newLink.type = 'text/css';
		document.head.appendChild(newLink);
		newLink.href = url;
	}
}
